import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

import {
  registerStart,
  registerSuccess,
  registerFailure,
  authRdv,
  loginSuccess,
} from "../../store/slices/userSlice";
import { useNavigate } from "react-router-dom";

import Checkbox from "../../components/inputs/checkbox";
import { FormCheck } from "react-bootstrap";
import { toast } from "react-toastify";
import LocationAutocomplete from "../../components/LocationAutocomplete";
import { useLoadScript } from "@react-google-maps/api";
import { GoogleOAuthProvider } from "@react-oauth/google";
import GoogleLoginButton from "../../components/googleBtn";
import Divider from "../../components/divider";

const RegisterForm = ({ from = "register", id, after }) => {
  const libraries = ["places"];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const { loading, error } = useSelector((state) => state.user);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  // Validation Schema
  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Email invalide").required("L'email est requis"),
    name: Yup.string().required("Le nom est requis"),
    prenom: Yup.string().required("Le prénom est requis"),
    telephone: Yup.string()
      .required("Le numéro de téléphone est requis")
      .test("is-valid-phone", "Numéro de téléphone invalide", (value) => {
        if (!value) return false; // Required field
        return isValidPhoneNumber(value);
      }),
    password: Yup.string()
      .min(8, "Le mot de passe doit contenir au moins 8 caractères")
      .required("Le mot de passe est requis"),
    legalAccepted: Yup.boolean()
      .oneOf([true], "Vous devez accepter les conditions générales")
      .required("Vous devez accepter les conditions générales"),
    isProfessional: Yup.boolean(),
    // Professional fields (conditionally required)
    siret: Yup.string().when("isProfessional", {
      is: true,
      then: (schema) =>
        schema
          .required("Le SIRET est requis pour les professionnels")
          .matches(/^\d{14}$/, "Le SIRET doit contenir exactement 14 chiffres")
          .test(
            "api-check",
            "Le SIRET n'existe pas dans la base INSEE",
            async (value) => {
              try {
                const response = await fetch(`https://api.insee.fr/api-sirene/3.11/siret/${value}`, {
                  method: 'GET',
                  headers: {
                    // 'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                    'X-INSEE-Api-Key-Integration' : `${process.env.REACT_APP_CONSUMER_KEY}`
                  },
                });
                return response.ok; // true si le SIRET existe
              } catch {
                return false;
              }
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    companyName: Yup.string().when("isProfessional", {
      is: true,
      then: (schema) =>
        schema.required("La dénomination est requise pour les professionnels"),
      otherwise: (schema) => schema.notRequired(),
    }),

    companyAddress: Yup.string().when("isProfessional", {
      is: true,
      then: (schema) =>
        schema.required("L'adresse est requise pour les professionnels"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });


  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      dispatch(registerStart());

      const userData = {
        name: values.name,
        email: values.email,
        password: values.password,
        password_confirmation: values.password,
        phone_number: values.telephone,
        nom: values.name,
        prenom: values.prenom,
        is_professional: values.isProfessional,
        siret: values.isProfessional ? values.siret : null,
        company_name: values.isProfessional ? values.companyName : null,
        company_address: values.isProfessional ? values.companyAddress : null,
        newsletter: values.siteAccepted,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}register`,
        userData
      );

      dispatch(registerSuccess());
      if (response.status === 201) {
        // toast.success("Inscription");
        if (from === "rdv") {
          const { token, user } = response.data;
          dispatch(loginSuccess({ user, token }));
          dispatch(authRdv(true));
          after();
          // navigate(`/facturation/${id}`)
        } else {
          const { token, user, adresse_facturation } = response.data;
          dispatch(loginSuccess({ adresse_facturation, user, token }));
          navigate("/compte/informations-personnel");
        }
      } else {
        toast.error("Une erreur est survenue lors de l'inscription");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Échec de l'inscription";
      dispatch(registerFailure(errorMessage));
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLocationSelect = (place, setFieldValue, setFieldTouched) => {
    if (!place) {
      setFieldValue("companyAddress", "");
      setFieldValue("lat", null);
      setFieldValue("lng", null);
      setFieldValue("postalCode", null);
      setFieldValue("city", null);
    } else {
      setFieldValue("companyAddress", place.description);

      if (isLoaded && window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ placeId: place.place_id }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
            const { lat: googleLat, lng: googleLng } =
              results[0].geometry.location;
            let city = "";
            let postalCode = "";

            results[0].address_components.forEach((component) => {
              if (component.types.includes("locality")) {
                city = component.long_name;
              }
              if (component.types.includes("postal_code")) {
                postalCode = component.long_name;
              }
            });

            setFieldValue("lat", googleLat());
            setFieldValue("lng", googleLng());
            setFieldValue("postalCode", postalCode);
            setFieldValue("city", city);
          }
        });
      }
    }
    setFieldTouched("companyAddress", true);
  };

  return (
    <Formik
      initialValues={{
        email: "",
        name: "",

        prenom: "",
        telephone: "",
        password: "",
        isProfessional: false,
        siret: "",
        companyName: "",
        companyAddress: "",
        legalAccepted: false,
        siteAccepted: false,
        lat: null,
        lng: null,
        postalCode: null,
        city: null,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
      }) => (
        <GoogleOAuthProvider
          clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
          redirectUri={window.location.origin}
        >
          <div
            className={`mt-38 w-100 mb-24 ${
              from === "rdv" ? "p-24 bg-white" : ""
            }`}
          >
            <h3 className="grey500 mb-0">
              Inscription
              {values.isProfessional && (
                <span className="tag-user ml-8">Client pro</span>
              )}
            </h3>

            <Form className="register-form w-100 mt-24">
              {/* Email Field */}
              <div className="input-wrapper ">
                <label className="form-label">Email*</label>
                <Field
                  name="email"
                  type="email"
                  className={` ${
                    touched.email && errors.email ? "is-invalid" : ""
                  }`}
                  placeholder="john@gmail.com"
                />
                <ErrorMessage name="email">
                  {(msg) => (
                    <div className="error-message mt-1">
                      <img
                        src="/images/alert-circle.svg"
                        className="arrow mr-8"
                        alt="Arrow"
                      />
                      {msg}
                    </div>
                  )}
                </ErrorMessage>
              </div>

              {/* Name Fields */}
              <div className="row">
                <div className="col-md-6 input-wrapper">
                  <label className="form-label">Nom*</label>
                  <Field
                    name="name"
                    type="text"
                    className={` ${
                      touched.name && errors.name ? "is-invalid" : ""
                    }`}
                    placeholder="Nom"
                  />
                  <ErrorMessage name="name">
                    {(msg) => (
                      <div className="error-message mt-1">
                        <img
                          src="/images/alert-circle.svg"
                          className="arrow mr-8"
                          alt="Arrow"
                        />
                        {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>
                <div className="col-md-6 input-wrapper  ">
                  <label className="form-label">Prénom*</label>
                  <Field
                    name="prenom"
                    type="text"
                    className={` ${
                      touched.prenom && errors.prenom ? "is-invalid" : ""
                    }`}
                    placeholder="Prénom"
                  />
                  <ErrorMessage name="prenom">
                    {(msg) => (
                      <div className="error-message mt-1">
                        <img
                          src="/images/alert-circle.svg"
                          className="arrow mr-8"
                          alt="Arrow"
                        />
                        {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>
              </div>

              {/* Phone Field */}
              <div className="input-wrapper">
                <label className="form-label">Numéro de téléphone*</label>
                <PhoneInput
                  international
                  defaultCountry="FR"
                  countries={["FR", "BE", "CH", "LU", "MC"]} // Limit to specific countries if needed
                  value={values.telephone}
                  onChange={(value) => {
                    setFieldValue("telephone", value);
                    // Validate immediately on change
                    if (value) {
                      const isValid = isValidPhoneNumber(value);
                      setFieldValue("isValidPhone", isValid);
                    }
                  }}
                  onBlur={() => {
                    setFieldTouched("telephone", true);
                    // Final validation on blur
                  }}
                  id="telephone"
                  name="telephone"
                  // className={`phone-input ${
                  //   touched.telephone && errors.telephone ? 'is-invalid' : ''
                  // }`}
                />
                <ErrorMessage name="telephone">
                  {(msg) => (
                    <div className="error-message mt-1">
                      <img
                        src="/images/alert-circle.svg"
                        className="arrow mr-8"
                        alt="Arrow"
                      />
                      {msg}
                    </div>
                  )}
                </ErrorMessage>
              </div>

              {/* Password Field */}
              <div className="input-wrapper password ">
                <label className="form-label">Mot de passe*</label>
                <div className="password-input-wrapper">
                  <Field
                    name="password"
                    type={isVisible ? "text" : "password"} // Toggle between text and password
                    // as={PasswordInput}
                    className={`${
                      touched.password && errors.password ? "is-invalid" : ""
                    }`}
                    placeholder="Mot de passe"
                  />
                  <span
                    className="visibility-toggle"
                    onClick={toggleVisibility}
                  >
                    <img
                      src={
                        isVisible
                          ? "/images/eye-closed.svg"
                          : "/images/eye-open.png"
                      }
                      alt="eye toggle"
                      width={24}
                      height={24}
                    />
                  </span>
                </div>
                <ErrorMessage name="password">
                  {(msg) => (
                    <div className="error-message mt-8">
                      <img
                        src="/images/alert-circle.svg"
                        className="arrow mr-8"
                        alt="Arrow"
                      />
                      {msg}
                    </div>
                  )}
                </ErrorMessage>
                {(!touched.password || !errors.password) && (
                  <div className="mt-8 legend grey66">
                    Le mot de passe doit faire plus de 8 caractères
                  </div>
                )}
              </div>

              {/* Professional Switch */}
              <div className="additional-fields ">
                <div className="custom-switch custom-switch-label-io d-flex align-items-center">
                  <FormCheck
                    type="switch"
                    id="custom-switch"
                    className="form-switch"
                    checked={values.isProfessional}
                    onChange={(e) =>
                      setFieldValue("isProfessional", e.target.checked)
                    }
                  />
                  <label className="grey66 body" htmlFor="custom-switch">
                    Je suis un client professionnel
                  </label>
                </div>

                {/* Professional Fields */}
                {values.isProfessional && (
                  <div className="mt-3">
                    <h5 className="grey500">Informations de l'entreprise</h5>

                    {/* SIRET Field */}
                    <div className="input-wrapper mt-8">
                      <label className="form-label">SIRET*</label>
                      <Field
                        name="siret"
                        type="text"
                        className={` ${
                          touched.siret && errors.siret ? "is-invalid" : ""
                        }`}
                        placeholder="000 000 000 00000"
                      />
                      <ErrorMessage name="siret">
                        {(msg) => (
                          <div className="error-message mt-1">
                            <img
                              src="/images/alert-circle.svg"
                              className="arrow mr-8"
                              alt="Arrow"
                            />
                            {msg}
                          </div>
                        )}
                      </ErrorMessage>
                    </div>

                    {/* Company Name Field */}
                    <div className="input-wrapper mt-8">
                      <label className="form-label">Dénomination*</label>
                      <Field
                        name="companyName"
                        type="text"
                        className={` ${
                          touched.companyName && errors.companyName
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder="Dénomination"
                      />
                      <ErrorMessage name="companyName">
                        {(msg) => (
                          <div className="error-message mt-1">
                            <img
                              src="/images/alert-circle.svg"
                              className="arrow mr-8"
                              alt="Arrow"
                            />
                            {msg}
                          </div>
                        )}
                      </ErrorMessage>
                    </div>

                    {/* Company Address Field */}
                    <div className="input-wrapper mt-8">
                      <label className="form-label">
                        Adresse de l'établissement*
                      </label>
                      <div className="relative">
                        <LocationAutocomplete
                          onLocationSelect={(place) =>
                            handleLocationSelect(
                              place,
                              setFieldValue,
                              setFieldTouched
                            )
                          }
                          isGoogleLoaded={isLoaded}
                          value={values.companyAddress}
                        />
                      </div>

                      <ErrorMessage name="companyAddress">
                        {(msg) => (
                          <div className="error-message mt-1">
                            <img
                              src="/images/alert-circle.svg"
                              className="arrow mr-8"
                              alt="Arrow"
                            />
                            {msg}
                          </div>
                        )}
                      </ErrorMessage>
                    </div>
                  </div>
                )}
              </div>

              {/* Legal Checkbox */}
              <div className="form-group ">
                <Field
                  name="legalAccepted"
                  component={Checkbox}
                  id="legal-checkbox"
                  label="En créant mon compte, j'accepte les"
                  links={[
                    { text: "CGV", url: "/cgv" },
                    { text: ", " },
                    { text: "CGU ", url: "/cgu" },
                    { text: " et la " },
                    {
                      text: "Politique de confidentialité",
                      url: "/politique-confidentialite",
                    },
                  ]}
                  onChange={(e) =>
                    setFieldValue("legalAccepted", e.target.checked)
                  }
                  checked={values.legalAccepted || false}
                />
                <ErrorMessage name="legalAccepted">
                  {(msg) => (
                    <div className="error-message mt-1">
                      <img
                        src="/images/alert-circle.svg"
                        className="arrow mr-8"
                        alt="Arrow"
                      />
                      {msg}
                    </div>
                  )}
                </ErrorMessage>
              </div>

              {/* Newsletter Checkbox */}
              <div className="form-group mb-4">
                <Field
                  name="siteAccepted"
                  as={Checkbox}
                  id="site-checkbox"
                  label="J'accepte de recevoir des communications commerciales Twyny"
                  onChange={(e) =>
                    setFieldValue("siteAccepted", e.target.checked)
                  }
                  checked={values.siteAccepted || false}
                />
              </div>

              {/* Submit Button */}

              <button
                type="submit"
                className="twyny-cta-primary w-100"
                disabled={isSubmitting || loading}
              >
                {isSubmitting || loading
                  ? "Enregistrement..."
                  : "Créer mon compte"}
              </button>
              {from === "login" && (
                <>
                  <div className="signup-container mt-32">
                    <p className="body grey66 mr-8 mb-0">
                      Vous avez déjà un compte ?
                      <a href="/login" className="link-blue ml-8">
                        Connectez-vous ici
                      </a>
                    </p>
                  </div>
                </>
              )}
            </Form>
            <Divider />
            {from === "rdv" && (
              <>
                <button
                  onClick={() => navigate(`/identification/${id}`)}
                  className="twyny-cta-secondary w-100 "
                >
                  Se connecter
                </button>
                {/* <button
                  onClick={() => navigate(`/compte-invite/${id}`)}
                  className="twyny-cta-secondary w-100 mt-24"
                >
                  Continuer en tant qu’invité
                </button> */}
                <div className="mt-24"></div>
              </>
            )}
            <GoogleLoginButton from={from} id={id} after={after} />
          </div>
          {/* Add Google Sign-In button */}
        </GoogleOAuthProvider>
      )}
    </Formik>
  );
};

export default RegisterForm;
